import { z } from 'zod/v4';

/**
 * Google Analytics event settings sub-group. Mirrors `GoogleSettings` from
 * `common/types/analytics-setings.type.ts`. Every leaf is `nullish()` —
 * the host form factory (`analyticsSettingsFormGroup`) initialises all
 * controls to `null` and only fills what the user configures.
 */
const googleSettingsSchemaLegacy = z.object({
    category: z.string().nullish(),
    action: z.string().nullish(),
    label: z.string().nullish(),
    currency: z.string().nullish(),
    value: z.number().nullish(),
    leadSource: z.string().nullish(),
});

/**
 * Facebook Pixel event settings sub-group. Mirrors `PixelSettings`; the
 * `type` fields hold `EPixelTypes` values — kept as plain `z.string()` here
 * so the package doesn't have to track the host enum's 17 literals.
 */
const pixelSettingsSchemaLegacy = z.object({
    type: z.string().nullish(),
    typeAfterPayment: z.string().nullish(),
    currency: z.string().nullish(),
    value: z.string().nullish(),
    content_ids: z.string().nullish(),
    content_name: z.string().nullish(),
    content_type: z.string().nullish(),
});

/**
 * Per-element analytics settings — matches the FormGroup produced by
 * `analyticsSettingsFormGroup()` in the host app
 * (`web-analytics-settings.form.ts`), which is attached to buttons, forms,
 * and other clickable widgets.
 */
export const analyticsSettingsSchemaLegacy = z
    .object({
        googleAnalytic: z.boolean().nullish(),
        googleAnalyticAfterPayment: z.boolean().nullish(),
        pixelAnalytic: z.boolean().nullish(),
        pixelAnalyticAfterPayment: z.boolean().nullish(),
        googleSettings: googleSettingsSchemaLegacy.nullish(),
        pixelSettings: pixelSettingsSchemaLegacy.nullish(),
    })
    .describe('Click-tracking analytics settings (Google Analytics / Facebook Pixel).');

export type AnalyticsSettingsLegacy = z.infer<typeof analyticsSettingsSchemaLegacy>;

/**
 * Shape fragment for nodes that carry the per-element analytics group
 * (button items today, form widget data later) — `nullish()` because
 * analytics post-dates the widgets. Spread into the node's `z.object`.
 * Form twin: `analyticsSettingsPlugin` in `@sites/entity-signal-form`
 * (`plugins/widget/analytics-settings.plugin.ts`).
 */
export const analyticsSettingsPluginShapeLegacy = {
    analyticsSettings: analyticsSettingsSchemaLegacy
        .nullish()
        .describe('Click-tracking analytics settings for this element.'),
} as const;

/**
 * Default analytics bag — all nulls, matching the host form factory's
 * initial state (nothing tracked until the user opts in).
 */
export const createAnalyticsSettingsLegacy = (
    overrides: Partial<AnalyticsSettingsLegacy> = {}
): AnalyticsSettingsLegacy => ({
    googleAnalytic: null,
    googleAnalyticAfterPayment: null,
    pixelAnalytic: null,
    pixelAnalyticAfterPayment: null,
    googleSettings: {
        category: null,
        action: null,
        label: null,
        currency: null,
        value: null,
        leadSource: null,
    },
    pixelSettings: {
        type: null,
        typeAfterPayment: null,
        currency: null,
        value: null,
        content_ids: null,
        content_name: null,
        content_type: null,
    },
    ...overrides,
});
